/**
 * Base de conhecimento PÚBLICA (/kb e /kb/$slug) — páginas limpas fora do
 * AppLayout e sem auth (o AuthGate faz bypass do Keycloak nessas rotas).
 * Consomem apenas os endpoints /api/v1/public/* (fetch sem bearer).
 */
import { useState } from 'react';
import { getRouteApi, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Folder } from 'lucide-react';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { PublicBrandMark } from '@/shared/brand-mark';
import {
  errorMessage,
  publicKbArticleOptions,
  publicKbListOptions,
} from '@/features/knowledge/knowledge-queries';
import { usePublicKbFeedback } from '@/features/knowledge/knowledge-mutations';
import { formatDateTime, initialOf } from '@/features/knowledge/knowledge-format';
import {
  FeedbackBox,
  KnowledgeAttachments,
  KnowledgeHtml,
} from '@/features/knowledge/knowledge-article-view';

const articleRouteApi = getRouteApi('/kb/$slug');

/** Shell centralizado: wordmark petróleo + subtítulo, conteúdo max-w-3xl. */
function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-background">
      <header className="flex flex-col items-center gap-1 pt-10 pb-8">
        <Link to="/kb" aria-label="Base de conhecimento" className="flex items-center">
          {/* marca do tenant (spec 017) em petróleo sobre o canvas claro */}
          <PublicBrandMark className="text-2xl text-sidebar" iconClassName="size-6" />
        </Link>
        <span className="text-sm text-muted-foreground">Base de conhecimento</span>
      </header>
      <main className="mx-auto w-full max-w-3xl px-4 pb-16">{children}</main>
    </div>
  );
}

// ── /kb — lista de artigos públicos ─────────────────────────────────────────
export function PublicKbListPage() {
  const list = useQuery(publicKbListOptions());

  return (
    <PublicShell>
      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        {list.isPending ? (
          <div aria-busy className="divide-y divide-divider">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="space-y-2 p-4">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-48" />
              </div>
            ))}
          </div>
        ) : list.isError ? (
          <EmptyState message={`Erro ao carregar a base: ${errorMessage(list.error)}`} />
        ) : list.data.length === 0 ? (
          <EmptyState message="Não há dados" />
        ) : (
          <div className="divide-y divide-divider">
            {list.data.map((item) => (
              <div key={item.slug} className="p-4">
                <Link
                  to="/kb/$slug"
                  params={{ slug: item.slug }}
                  className="text-[15px] font-semibold text-primary transition-colors hover:text-primary-pressed"
                >
                  {item.title}
                </Link>
                <div className="mt-0.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                  <Folder className="size-3.5" />
                  {item.folderName}
                  <span aria-hidden>·</span>
                  <span className="tabular-nums">{formatDateTime(item.updatedAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicShell>
  );
}

// ── /kb/$slug — artigo público ──────────────────────────────────────────────
export function PublicKbArticlePage() {
  const { slug } = articleRouteApi.useParams();
  const article = useQuery(publicKbArticleOptions(slug));
  const feedback = usePublicKbFeedback(slug);
  // O endpoint público não devolve contagens/voto — controlamos o voto local
  // do visitante para o "Obrigado pelo feedback!" e a troca de voto.
  const [myVote, setMyVote] = useState<boolean | null>(null);

  return (
    <PublicShell>
      <div className="mb-3">
        <Link
          to="/kb"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary-pressed"
        >
          <ArrowLeft className="size-4" />
          Voltar
        </Link>
      </div>

      {article.isPending ? (
        <div aria-busy className="space-y-4 rounded-lg border bg-card p-6 shadow-xs">
          <Skeleton className="h-8 w-2/3" />
          <div className="flex items-center gap-3">
            <Skeleton className="size-9 rounded-full" />
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-56" />
            </div>
          </div>
          <Skeleton className="h-48 w-full" />
        </div>
      ) : article.isError ? (
        <div className="rounded-lg border bg-card shadow-xs">
          <EmptyState message={`Artigo não encontrado (${errorMessage(article.error)})`} />
        </div>
      ) : (
        <article className="space-y-5 rounded-lg border bg-card p-6 shadow-xs">
          <h1 className="text-2xl font-semibold">{article.data.title}</h1>

          <div className="flex flex-wrap items-center gap-3">
            <span
              aria-hidden
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground"
            >
              {initialOf(article.data.authorName)}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold">
                Escrito por {article.data.authorName ?? '—'}
              </div>
              <div className="text-sm text-muted-foreground">
                Atualizado em {formatDateTime(article.data.updatedAt)}
              </div>
            </div>
            <span className="ml-auto inline-flex items-center gap-1 text-[13px] text-muted-foreground">
              <Folder className="size-3.5" />
              {article.data.folderName}
            </span>
          </div>

          <KnowledgeHtml html={article.data.body} />

          <KnowledgeAttachments files={article.data.files} />

          <FeedbackBox
            myVote={myVote}
            pending={feedback.isPending}
            onVote={(helpful) => feedback.mutate(helpful, { onSuccess: () => setMyVote(helpful) })}
          />
        </article>
      )}
    </PublicShell>
  );
}
